import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { externalEntityService } from "../../services/externalEntityService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { ArrowLeft } from "lucide-react"
import Button from '../common/Button.js'
import { useTranslation } from 'react-i18next';
import { formatAuditUser } from '../../utils/userDisplay';


const ViewExternalML = () => {
  const { t } = useTranslation();
  const { externalMLId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [mlSheet, setMlSheet] = useState(null)
  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchExternalML()
  }, [externalMLId])

  const fetchExternalML = async () => {
    try {
      setLoading(true)
      // GET /api/external-entities/ml-sheets/<external_ml_id>
      // Response: { success, ml_sheet: { ...fields, shipments: [...] } }
      const response = await externalEntityService.getExternalML(externalMLId)
      const ml = response.data.ml_sheet

      if (!ml) throw new Error("No data returned")

      setMlSheet(ml)
      // shipments are embedded directly in the ml_sheet object
      setShipments(Array.isArray(ml.shipments) ? ml.shipments : [])
    } catch (error) {
      console.error("Error fetching External ML:", error)
      Alert.error(error.response?.data?.message || "Failed to load External ML Sheet")
      navigate("/external-ml")
    } finally {
      setLoading(false)
    }
  }

  const getPaymentModeColor = (mode) => {
    const map = {
      to_pay: "bg-yellow-100 text-yellow-800 border-yellow-300",
      paid: "bg-green-100 text-green-800 border-green-300",
      paid_balance: "bg-blue-100 text-blue-800 border-blue-300",
    }
    return map[(mode || "").toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-300"
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading External ML Sheet..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!mlSheet) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-gray-600 text-lg font-semibold">External ML Sheet not found</p>
            <button
              onClick={() => navigate("/external-ml")}
              className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-semibold"
            >
              Back to External ML Sheets
            </button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>

                {/* Page Header */}
                <div className="p-4 sm:p-2 sm:py-0 mb-6">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary">
                        External ML #{mlSheet.external_ml_id}
                      </h1>
                      <p className="text-gray-700 text-sm sm:text-base">
                        {mlSheet.trip_sheet_no ? `Trip Sheet: ${mlSheet.trip_sheet_no}` : ""}
                        {mlSheet.created_at
                          ? ` · Created ${new Date(mlSheet.created_at).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}`
                          : ""}
                      </p>
                    </div>
                    <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/external-ml")}>Back</Button>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Total To Pay</div>
                    <div className="text-xl font-bold text-secondary">
                      ₹{(mlSheet.total_topay || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">{t('pages.shipment.title')}</div>
                    <div className="text-xl font-bold text-secondary">
                      {mlSheet.external_shipment_count || shipments.length}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-purple-500">
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Driver</div>
                    <div className="text-sm font-semibold text-secondary break-all">
                      {mlSheet.driver_name || "—"}
                    </div>
                    {mlSheet.driver_contact && (
                      <div className="text-xs text-gray-500">{mlSheet.driver_contact}</div>
                    )}
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
                    <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Trip Sheet No</div>
                    <div className="text-sm font-semibold text-secondary">
                      {mlSheet.trip_sheet_no || "—"}
                    </div>
                  </div>
                </div>

                {/* Detail Info */}
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md mb-6">
                  <h3 className="text-sm font-semibold text-secondary mb-4 pb-2 border-b border-gray-200">
                    ML Sheet Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">ML Sheet ID</div>
                      <div className="text-sm text-secondary font-regular">{mlSheet.external_ml_id}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Trip Sheet No</div>
                      <div className="text-sm text-secondary font-regular">{mlSheet.trip_sheet_no || "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Driver Name</div>
                      <div className="text-sm text-secondary font-regular">{mlSheet.driver_name || "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Driver Contact</div>
                      <div className="text-sm text-secondary font-regular">{mlSheet.driver_contact || "—"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">{t('common.createdBy')}</div>
                      <div className="text-sm text-secondary font-regular">
                        {formatAuditUser(mlSheet, 'created_by')}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-primary">
                      <div className="text-gray-500 text-xs mb-1">Created At</div>
                      <div className="text-sm text-secondary font-regular">
                        {mlSheet.created_at
                          ? new Date(mlSheet.created_at).toLocaleString("en-IN")
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipments Table */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-secondary">
                      External Shipments ({shipments.length})
                    </h3>
                  </div>

                  {shipments.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-lg font-semibold">No shipments found</p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop */}
                      <div className="hidden md:block w-full overflow-x-auto">
                        <table className="w-full table-fixed border-collapse">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                              <th className="px-4 py-3 text-xs font-semibold w-[5%] text-center">#</th>
                              <th className="px-4 py-3 text-xs font-semibold w-[14%] text-center">Shipment ID</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">LR No</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Consignor</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Consignee</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">To Station</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Total</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Balance</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Payment Mode</th>
                              <th className="px-4 py-3 text-xs font-semibold text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {shipments.map((s, index) => (
                              <tr
                                key={s.external_shipment_id}
                                className={`hover:bg-gray-50 text-center transition-colors ${
                                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                }`}
                              >
                                <td className="px-5 py-3 text-sm">{index + 1}</td>
                                <td className="px-5 py-3 text-center">
                                  <span
                                    onClick={() =>
                                      navigate(
                                        `/external-ml/${externalMLId}/shipment/${s.external_shipment_id}`
                                      )
                                    }
                                    className="text-secondary font-regular cursor-pointer text-sm hover:text-blue-600 hover:underline"
                                  >
                                    {s.external_shipment_id}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600 text-center">{s.lr_no || "—"}</td>
                                <td className="px-5 py-3 text-sm text-gray-600 text-center">{s.consignor || "—"}</td>
                                <td className="px-5 py-3 text-sm text-gray-600 text-center">{s.consignee || "—"}</td>
                                <td className="px-5 py-3 text-sm text-gray-600 text-center">{s.to_station || "—"}</td>
                                <td className="px-5 py-3 text-sm text-center font-semibold">
                                  ₹{(s.total_amount || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-sm text-center text-orange-600 font-semibold">
                                  ₹{(s.balance_amount || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  <span
                                    className={`px-2 py-1 text-xs rounded border ${getPaymentModeColor(
                                      s.payment_mode
                                    )}`}
                                  >
                                    {s.payment_mode || "—"}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-center">
                                  <button
                                    onClick={() =>
                                      navigate(
                                        `/external-ml/${externalMLId}/shipment/${s.external_shipment_id}`
                                      )
                                    }
                                    className="text-secondary hover:text-blue-600 font-semibold text-sm"
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="md:hidden divide-y divide-gray-200">
                        {shipments.map((s) => (
                          <div key={s.external_shipment_id} className="p-4 hover:bg-gray-50">
                            <div className="flex justify-between items-start mb-2">
                              <span
                                onClick={() =>
                                  navigate(
                                    `/external-ml/${externalMLId}/shipment/${s.external_shipment_id}`
                                  )
                                }
                                className="text-secondary font-semibold cursor-pointer hover:text-blue-600 hover:underline text-sm"
                              >
                                {s.external_shipment_id}
                              </span>
                              <button
                                onClick={() =>
                                  navigate(
                                    `/external-ml/${externalMLId}/shipment/${s.external_shipment_id}`
                                  )
                                }
                                className="text-blue-600 text-sm font-medium"
                              >
                                View
                              </button>
                            </div>
                            <div className="text-xs text-gray-600 space-y-1">
                              <div>LR No: {s.lr_no || "—"}</div>
                              <div>Consignor: {s.consignor || "—"}</div>
                              <div>Consignee: {s.consignee || "—"}</div>
                              <div>To Station: {s.to_station || "—"}</div>
                              <div>Total: ₹{(s.total_amount || 0).toFixed(2)}</div>
                              <div>Balance: ₹{(s.balance_amount || 0).toFixed(2)}</div>
                              <div>Mode: {s.payment_mode || "—"}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

    </DashboardLayout>
  )
}

export default ViewExternalML